// Section 24.6: content registries. Every game noun is an entry built by a define* function
// that validates its shape with zod at module load and returns a deep-frozen object.
// The schema is the single description of a valid entry.
import { z } from 'zod';
import { SimError } from './errors';
import * as schemas from './registry-schemas';
import type { Contributor, ModifierKeys, ModifierPipeline } from './modifiers';
import type {
  ActiveEffect,
  Building,
  BuildingTypeId,
  Delta,
  EventId,
  EventPrompt,
  IllnessId,
  JobId,
  JobRiskKind,
  Modifier,
  NormId,
  Person,
  PersonId,
  Policies,
  PolicyId,
  PreviewLine,
  PriorityGroupId,
  PromptAnswer,
  RationPolicyId,
  RationResource,
  ReadContext,
  ReportEntry,
  Resource,
  Season,
  ServiceKind,
  Target,
  TargetKind,
  TraitId,
  ValidationProblem,
} from './types';

// ---------------------------------------------------------------- shared shapes

type Hook<A extends unknown[], R> = (...args: A) => R;
type ModifiersHook = Hook<[ReadContext, Target], readonly Modifier[]>;

export const noModifiers: ModifiersHook = () => [];

interface Common<Id extends string> {
  id: Id;
  label: string;
  /** Spec section the entry implements, e.g. 'S8'. */
  section: string;
}

interface Modifies {
  /** Target kinds `modifiers` may affect; the pipeline calls it only for these. */
  modifierTargets: readonly TargetKind[];
  /**
   * Optional (M6): for some of those kinds, the only jobs, resources or causes it may affect
   * (core/modifiers.ts ModifierKeys); the pipeline skips it for the rest (Section 2 budget).
   */
  modifierKeys?: ModifierKeys;
  modifiers: ModifiersHook;
}

// ---------------------------------------------------------------- definitions

export type JobOutput =
  | { kind: 'stock'; resource: Resource }
  | { kind: 'service'; service: ServiceKind }
  | { kind: 'none' };

/** Section 8. */
export interface JobDef extends Common<JobId>, Modifies {
  heavy: boolean;
  output: JobOutput;
  /** Base yield per fully productive worker per season (per plot for farmers). */
  baseYield: number;
  /** Tool multiplier when the worker has a tool; null if the job uses no tool. */
  toolFactor: number | null;
  /** Extra tool wear per season beyond normal use (hunter 0.1, guard 0.05). */
  toolWear: number;
  risks: Partial<Record<JobRiskKind, number>>;
  /** Illness caught on a successful `illness` risk roll (fouled water: flux). */
  riskIllness: IllnessId | null;
  requiresBuilding: BuildingTypeId | null;
  minStrength: number;
  /** Deltas for one worker given their evaluated output this season. */
  produce: Hook<[ReadContext, Person, number], readonly Delta[]>;
  /** Job-specific eligibility beyond the generic age/heavy/building rules. */
  eligible: Hook<[ReadContext, Person], boolean>;
}

export type BuildingLimit =
  { kind: 'none' } | { kind: 'fixed'; max: number } | { kind: 'per-people'; people: number };

/** Section 9. */
export interface BuildingDef extends Common<BuildingTypeId>, Modifies {
  buildPoints: number;
  materials: Partial<Record<Resource, number>>;
  /** Fire returns stone buildings to 60% and wood ones to 20% (Section 9). */
  material: 'wood' | 'stone';
  beds: number;
  plots: number;
  /** Stock capacity added per building (Section 7). */
  capacity: Partial<Record<Resource, number>>;
  limit: BuildingLimit;
  onComplete: Hook<[ReadContext, Building], readonly Delta[]>;
  onDamaged: Hook<[ReadContext, Building, number], readonly Delta[]>;
}

export interface EventChoiceDef {
  id: string;
  label: string;
  preview: Hook<[ReadContext, EventPrompt], readonly PreviewLine[]> | null;
  apply: Hook<
    [ReadContext, EventPrompt, Extract<PromptAnswer, { kind: 'event' }>],
    readonly Delta[]
  >;
}

/** Section 19. Events are data files; adding one never touches the engine. */
export interface EventDef extends Common<EventId> {
  /** Base chance per season (config.events[id].baseChance). */
  baseWeight: number;
  /** Seasons the event is biased towards; all four means "any". */
  seasons: readonly Season[];
  foreshadow: { chance: number } | null;
  ledgerTag: string | null;
  weight: Hook<[ReadContext], number>;
  text: Hook<[ReadContext], ReportEntry>;
  /** Immediate effect when drawn (the part that needs no choice). */
  apply: Hook<[ReadContext], readonly Delta[]>;
  choices: readonly EventChoiceDef[];
  modifierTargets: readonly TargetKind[];
  /** Modifiers while one of this event's effects is in force. */
  modifiers: Hook<[ReadContext, Target, ActiveEffect], readonly Modifier[]>;
}

export type PolicyGroup =
  'rations' | 'health' | 'work' | 'housing' | 'education' | 'society' | 'justice';

/** Section 18. */
export interface PolicyDef<K extends PolicyId = PolicyId> extends Common<K>, Modifies {
  group: PolicyGroup;
  /** The selectable options, or null for structured values (priority order, subjects). */
  options: readonly Policies[K][] | null;
  default: Policies[K];
  /** One-time trust change for everyone when switching (<= 0). */
  changeCost: Hook<[Policies[K], Policies[K]], number>;
  affects: Hook<[ReadContext, Policies[K]], readonly PreviewLine[]>;
  validate: Hook<[ReadContext, Policies[K]], ValidationProblem | null>;
}

export type PolicyRegistry = { readonly [K in PolicyId]: PolicyDef<K> };

/** Section 5. */
export interface TraitDef extends Common<TraitId>, Modifies {
  /** True if this trait forbids the job (Gentle will not guard). */
  restricts: Hook<[ReadContext, Person, JobId], boolean>;
}

/** Section 12. */
export interface IllnessDef extends Common<IllnessId>, Modifies {
  source: 'roll' | 'job' | 'hunger' | 'event';
  /** Share of a successful illness roll (roll-sourced illnesses only). */
  weight: number;
  untreatedSeasons: number;
  treatedSeasons: number;
  deathChance: number;
  contagious: boolean;
  onCatch: Hook<[ReadContext, Person], readonly Delta[]>;
  onRecover: Hook<[ReadContext, Person], readonly Delta[]>;
}

/** Section 16. */
export interface NormDef extends Common<NormId>, Modifies {
  establishAfter: number;
  decayAfter: number;
  /** Does this report entry count as a use of the precedent? */
  reinforcedBy: Hook<[ReadContext, ReportEntry], boolean>;
  /** Is this entry a contrary choice, which resets the run of uses (Section 16, Stream D)? */
  contradictedBy: Hook<[ReadContext, ReportEntry], boolean>;
}

/** Section 17. */
export interface PriorityGroupDef extends Common<PriorityGroupId> {
  members: Hook<[ReadContext], readonly PersonId[]>;
}

export interface AllocationInput {
  resource: RationResource;
  /** Groups in priority order; members sorted weakest first then by id. Everyone appears once. */
  ranked: readonly (readonly PersonId[])[];
  demand: Readonly<Record<PersonId, number>>;
  available: number;
  /** Floor as a fraction of need (floor-then-priority). */
  floor: number;
  /** Pins (fraction of need) honoured before the policy fills the rest. */
  pinned: Readonly<Record<PersonId, number>>;
}

export interface Allocation {
  /** Fraction of need 0..1 per person. */
  shares: Record<PersonId, number>;
  /** The order the policy proposes (for the shortage dialog). */
  order: PersonId[];
}

/** Section 17. */
export interface RationPolicyDef extends Common<RationPolicyId> {
  allocate: Hook<[ReadContext, AllocationInput], Allocation>;
}

export interface Registry {
  jobs: Readonly<Record<JobId, JobDef>>;
  buildings: Readonly<Record<BuildingTypeId, BuildingDef>>;
  events: Readonly<Record<EventId, EventDef>>;
  policies: PolicyRegistry;
  traits: Readonly<Record<TraitId, TraitDef>>;
  illnesses: Readonly<Record<IllnessId, IllnessDef>>;
  norms: Readonly<Record<NormId, NormDef>>;
  priorityGroups: Readonly<Record<PriorityGroupId, PriorityGroupDef>>;
  rationPolicies: Readonly<Record<RationPolicyId, RationPolicyDef>>;
  /** Core contributors (season, land, needs, age, skills, tools, events-in-effect). */
  contributors: readonly Contributor[];
  pipeline: ModifierPipeline;
}

// ---------------------------------------------------------------- define* builders

function deepFreeze<T>(value: T): T {
  if (value !== null && typeof value === 'object' && !Object.isFrozen(value)) {
    Object.freeze(value);
    for (const key of Object.keys(value)) deepFreeze((value as Record<string, unknown>)[key]);
  }
  return value;
}

function validated<T>(schema: z.ZodType, kind: string, def: T): Readonly<T> {
  const result = schema.safeParse(def);
  if (!result.success) {
    const id = (def as { id?: unknown }).id;
    throw new SimError(
      'registry',
      `invalid ${kind} '${String(id)}': ${z.prettifyError(result.error)}`,
      {
        ids: [String(id)],
      },
    );
  }
  return deepFreeze(def);
}

export function defineJob(def: JobDef): JobDef {
  return validated(schemas.jobSchema, 'job', def);
}

export function defineBuilding(def: BuildingDef): BuildingDef {
  return validated(schemas.buildingSchema, 'building', def);
}

export function defineEvent(def: EventDef): EventDef {
  const choiceIds = new Set(def.choices.map((c) => c.id));
  if (choiceIds.size !== def.choices.length) {
    throw new SimError('registry', `event '${def.id}' has duplicate choice ids`, { ids: [def.id] });
  }
  return validated(schemas.eventSchema, 'event', def);
}

export function definePolicy<K extends PolicyId>(def: PolicyDef<K>): PolicyDef<K> {
  const frozen = validated(schemas.policySchema, 'policy', def);
  if (def.options !== null) {
    const key = JSON.stringify(def.default);
    if (!def.options.some((o) => JSON.stringify(o) === key)) {
      throw new SimError('registry', `policy '${def.id}' default is not one of its options`, {
        ids: [def.id],
      });
    }
  }
  return frozen;
}

export function defineTrait(def: TraitDef): TraitDef {
  return validated(schemas.traitSchema, 'trait', def);
}

export function defineIllness(def: IllnessDef): IllnessDef {
  return validated(schemas.illnessSchema, 'illness', def);
}

export function defineNorm(def: NormDef): NormDef {
  return validated(schemas.normSchema, 'norm', def);
}

export function definePriorityGroup(def: PriorityGroupDef): PriorityGroupDef {
  return validated(schemas.priorityGroupSchema, 'priority group', def);
}

export function defineRationPolicy(def: RationPolicyDef): RationPolicyDef {
  return validated(schemas.rationPolicySchema, 'ration policy', def);
}

/** Core contributors (season, land, needs, age, skills, tools, events-in-effect). */
export function defineContributor(def: Contributor): Contributor {
  return validated(schemas.contributorSchema, 'contributor', def);
}

/** Adapt a content entry's `modifiers` hook into a pipeline contributor. */
export function contributorOf(
  slot: Contributor['slot'],
  namespace: string,
  def: Common<string> & Modifies,
): Contributor {
  return {
    slot,
    id: `${namespace}:${def.id}`,
    targets: def.modifierTargets,
    ...(def.modifierKeys === undefined ? {} : { only: def.modifierKeys }),
    modifiers: def.modifiers,
  };
}

/** Assert that a registry's keys equal its entries' ids (24.6 conventions). */
export function checkRegistryKeys(
  name: string,
  registry: Readonly<Record<string, { id: string }>>,
): void {
  for (const [key, def] of Object.entries(registry)) {
    if (key !== def.id) {
      throw new SimError(
        'registry',
        `${name} registry key '${key}' does not match entry id '${def.id}'`,
        {
          ids: [key],
        },
      );
    }
  }
}
